import { makeAutoObservable, runInAction } from 'mobx';
import createStoreContext from '../createStoreContext';
import { UserApiResponse } from '../../types/user';
import {
  checkOnboardingComplete,
  completeRevampOnboarding as completeRevampOnboardingApi,
  getUserInfoByCookie,
} from 'src/services/user';
import { userLogout } from 'src/services/auth';
import { getActionReviewCapability } from 'src/services/actionReview';
import type { ActionReviewCapability } from 'src/types/actionReview';

const ONBOARDING_COMPLETE_STORAGE_KEY = 'hana:onboarding:complete';

export const getOnboardingIdentityKey = (
  user?: Pick<UserApiResponse, '_id' | 'email' | 'organizationId'> | null,
): string | null => {
  const normalizedEmail = user?.email?.trim().toLowerCase();
  const normalizedOrganizationId =
    typeof user?.organizationId === 'string'
      ? user.organizationId.trim()
      : String(user?.organizationId ?? '');

  const organizationSegment = normalizedOrganizationId
    ? `|org:${normalizedOrganizationId}`
    : '';

  if (normalizedEmail) {
    return `email:${normalizedEmail}${organizationSegment}`;
  }

  const normalizedUserId =
    typeof user?._id === 'string' ? user._id.trim() : String(user?._id ?? '');

  return normalizedUserId
    ? `id:${normalizedUserId}${organizationSegment}`
    : null;
};

export const getOnboardingCompleteStorageKey = (
  identityKey?: string | null,
): string => {
  if (!identityKey) {
    return ONBOARDING_COMPLETE_STORAGE_KEY;
  }

  return `${ONBOARDING_COMPLETE_STORAGE_KEY}:${identityKey}`;
};

const clearLegacyOnboardingCompleteStatus = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(ONBOARDING_COMPLETE_STORAGE_KEY);
};

const getStoredOnboardingCompleteStatus = (
  identityKey?: string | null,
): boolean | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedValue = window.localStorage.getItem(
    getOnboardingCompleteStorageKey(identityKey),
  );

  if (storedValue === 'true') {
    return true;
  }

  if (storedValue === 'false') {
    return false;
  }

  return null;
};

const persistOnboardingCompleteStatus = (
  status: boolean | null,
  identityKey?: string | null,
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const storageKey = getOnboardingCompleteStorageKey(identityKey);

  if (status === null) {
    window.localStorage.removeItem(storageKey);
    clearLegacyOnboardingCompleteStatus();

    return;
  }

  window.localStorage.setItem(storageKey, status ? 'true' : 'false');
  clearLegacyOnboardingCompleteStatus();
};

export class AuthStoreState {
  private userDocument: UserApiResponse | null = null;
  private revampCapability: ActionReviewCapability | null = null;
  private _isOnBoardingComplete: boolean | null = null;
  private onboardingIdentityKey: string | null = null;
  private revampOnboardingCompletionRequest: {
    identityKey: string;
    promise: Promise<boolean>;
  } | null = null;
  private userFetchGeneration = 0;

  public constructor() {
    makeAutoObservable(this);
  }

  get userDetails() {
    return this.userDocument;
  }

  logout = async () => {
    this.userFetchGeneration += 1;

    try {
      await userLogout();
    } finally {
      runInAction(() => {
        this.userFetchGeneration += 1;
        this.userDocument = null;
        this.revampCapability = null;
        this._isOnBoardingComplete = null;
        this.onboardingIdentityKey = null;
        this.revampOnboardingCompletionRequest = null;
      });
    }
  };

  getUserDetails = () => {
    return this.userDocument;
  };

  getRevampCapability = () => {
    return this.revampCapability;
  };

  isRevampExperience = () => {
    return Boolean(this.revampCapability?.reviewEnabled);
  };

  getOnBoardingCompleteStatus = async () => {
    const currentIdentityKey = getOnboardingIdentityKey(this.userDocument);

    if (this.onboardingIdentityKey !== currentIdentityKey) {
      runInAction(() => {
        this._isOnBoardingComplete = null;
        this.onboardingIdentityKey = currentIdentityKey;
      });
    }

    if (this._isOnBoardingComplete !== null) {
      return this._isOnBoardingComplete;
    }

    const storedOnboardingStatus =
      getStoredOnboardingCompleteStatus(currentIdentityKey);

    if (storedOnboardingStatus !== null) {
      runInAction(() => {
        this._isOnBoardingComplete = storedOnboardingStatus;
      });

      return storedOnboardingStatus;
    }

    const {
      success: checkOnboardingCompleteSuccess,
      content: checkOnboardingCompleteApiResponse,
    } = await checkOnboardingComplete();

    if (
      !checkOnboardingCompleteSuccess ||
      !checkOnboardingCompleteApiResponse
    ) {
      return null;
    }

    runInAction(() => {
      this._isOnBoardingComplete =
        checkOnboardingCompleteApiResponse.isOnboardingCompleted;
      this.onboardingIdentityKey = currentIdentityKey;
    });
    persistOnboardingCompleteStatus(
      checkOnboardingCompleteApiResponse.isOnboardingCompleted,
      currentIdentityKey,
    );

    return checkOnboardingCompleteApiResponse.isOnboardingCompleted;
  };

  setOnBoardingCompleteStatus = (status: boolean) => {
    const currentIdentityKey = getOnboardingIdentityKey(this.userDocument);

    runInAction(() => {
      this._isOnBoardingComplete = status;
      this.onboardingIdentityKey = currentIdentityKey;
    });
    persistOnboardingCompleteStatus(status, currentIdentityKey);
  };

  completeRevampOnboarding = async () => {
    const identityKey = getOnboardingIdentityKey(this.userDocument);

    if (!this.isRevampExperience() || !identityKey) {
      return false;
    }

    if (this.revampOnboardingCompletionRequest?.identityKey === identityKey) {
      return this.revampOnboardingCompletionRequest.promise;
    }

    const promise = (async () => {
      const response = await completeRevampOnboardingApi();

      if (!response.success || !response.content?.isOnboardingCompleted) {
        return false;
      }

      // An organization switch can finish while the request is in flight.
      // Never let an old workspace response mark the new identity complete.
      if (getOnboardingIdentityKey(this.userDocument) !== identityKey) {
        return false;
      }

      this.setOnBoardingCompleteStatus(true);

      return true;
    })();

    this.revampOnboardingCompletionRequest = { identityKey, promise };

    try {
      return await promise;
    } finally {
      if (this.revampOnboardingCompletionRequest?.identityKey === identityKey) {
        runInAction(() => {
          this.revampOnboardingCompletionRequest = null;
        });
      }
    }
  };

  fetchUserDetails = async (): Promise<UserApiResponse | null> => {
    // This method updates the current user details without requiring the user to refresh the page.
    // It is used to promote the user to admin after DNS verification.
    const requestGeneration = ++this.userFetchGeneration;
    const [userInfoResponse, capabilityResponse] = await Promise.all([
      getUserInfoByCookie(),
      getActionReviewCapability().catch(() => null),
    ]);
    const { success: getUserInfoSuccess, content: userInfo } = userInfoResponse;

    if (requestGeneration !== this.userFetchGeneration) {
      return this.userDocument;
    }

    if (getUserInfoSuccess && userInfo) {
      // Resolve the active organization's server-governed rollout contract
      // before publishing the new user document. This makes organization
      // switches atomic: no newly selected workspace can inherit the prior
      // workspace's revamp shell, and no eligible workspace depends on a
      // duplicated client-side organization-id list.
      const nextRevampCapability =
        capabilityResponse?.success && capabilityResponse.content
          ? capabilityResponse.content
          : null;
      const nextIdentityKey = getOnboardingIdentityKey(userInfo);
      const didIdentityChange = this.onboardingIdentityKey !== nextIdentityKey;

      runInAction(() => {
        this.userDocument = userInfo;
        this.revampCapability = nextRevampCapability;

        if (didIdentityChange) {
          this._isOnBoardingComplete = null;
          this.onboardingIdentityKey = nextIdentityKey;
        }
      });

      return userInfo;
    } else {
      return null;
    }
  };
}

export const [AuthStoreProvider, useAuthStore] =
  createStoreContext(AuthStoreState);

export type AuthStore = ReturnType<typeof useAuthStore>;
